import './i18n/config';

import { Suspense, useEffect, useCallback, useState } from 'react';
import lazy from './chunk-loader';
import {
	createBrowserRouter,
	RouterProvider,
	Routes,
	Route,
	Navigate,
	useLocation
} from "react-router-dom";

// WARNING!!! Must be above Admin (and in fact any other component) otherwise it overrides styles of components
// WARNING!!! Broke the Landing page because the same components were loaded in Home.tsx and styles.scss overrode the Landing page
// WARNING!!! https://stackoverflow.com/a/63308877
import './styles.scss';
import 'react-virtualized/styles.css';

import Home from './components/root/home';
import { getAdminChannels, refreshToken } from './store/actions/authentication';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import LiveEvent from './components/live-event/live-event';
import WaitingIndicator from './components/general-ui/waiting-indicator/waiting-indicator';
import GoogleAuth from './components/google-auth/google-auth';
import SSORedirect from './components/sso-redirect/sso-redirect';
import PageNotFound from './components/404/404';
import MonitorPageVisibility from './utils/event-activity-timer';
import { useTypedSelector, useAppDispatch } from './store/reducers/use-typed-selector';
import AppContext, { AppContexts } from 'components/app-context';
import { ThemeContext } from 'components/live-event/theme-context';
import { EPaletteModes } from 'types/theme-packs';
import AuthGate from 'components/auth-gate/auth-gate';
import { useForceSecureCookies } from 'hooks/cookie.hooks';
import FeatureFlagGate from 'components/feature-flag-gate/feature-flag-gate';
import useMigrateColorPackToThemePack from 'use-migrate-color-pack-to-theme-packs.';
import useMigrateRegistrationGating from 'use-migrate-registration-gating';
import blAuth from 'connection/bl-auth/bl-auth';
import { PATHNAMES } from 'utils/admin-routing-utils';
import { useEventNameParam } from './utils/use-event-name-param';

import DomainUtils from './utils/admin-domain-utils';

const LemonadeGradient = lazy(() => import('./components/general-ui/lemonade-gradient'));

// import a worker by creating a blob from the text of it
// const testWorker = new Worker(new URL('./workers/test-worker.ts', import.meta.url), { type: 'module' });

const Admin = lazy(() => import('./components/admin/admin'));
const FullPageProjectThemeGradient = lazy(() => import('@general-ui/full-page-project-theme-gradient'));
const PlatformComponentRenderer = lazy(() => import('@admin/platform-component-renderer/platform-component-renderer'));
const Moderator = lazy(() => import('./components/moderation/moderation'));
const EmbedChat = lazy(() => import('./components/embed/chat'));
const EmbedCreatePlatform = lazy(() => import('./components/embed-create-platform/embed-create-platform'));
const SessionStreamEmbed = lazy(() => import('./components/live-event/session/session-stream/session-stream-embed'));
const Uploader = lazy(() => import('./components/general-ui/uploader/uploader'));
const IconPreviewer = lazy(() => import('./components/general-ui/icon-previewer'));
const SessionStreamEmbedDataProvider = lazy(() => import('./components/live-event/session/session-stream/session-stream-embed-data-provider'))
function LiveEventLanguageRedirect() {
	const eventName = useEventNameParam();
	const { search, hash } = useLocation();

	const baseLanguage = (window as any).eventBundle?.settings?.i18n?.base ?? 'en';

	return <Navigate to={`/${eventName}/${baseLanguage}${search}${hash}`} replace />;
}

const googleDomainRegex = /^google-auth\..*/; // Starts with 'google-auth.'
const oauthFlowRegex = /\/authentication\/presenter\/sign-in.*/;
const isTestEditor = window.location.href.startsWith('http://localhost:3000/admin');
const isEditor = DomainUtils.isEditor();
const isAdmin = DomainUtils.isAdmin();
const isGoogleAuth = googleDomainRegex.test(window.location.hostname);
const isOauthFlow = oauthFlowRegex.test(window.location.pathname);
const isProd = import.meta.env.VITE_STAGE === 'prod';
const isHot = import.meta.env.VITE_STAGE === 'hotfix';
const isLocal = import.meta.env.VITE_STAGE === 'local';

const isEmbed = window.location.href.includes('embed=true');
const isCreatePlatformEmbed = window.location.href.includes('embed-create-platform=true');

if (window.location.hostname.startsWith('www.brandlive')) {
	window.location.href = window.location.href.replace(/www/, 'admin');
}

let featureFlagToolImported = false;
// never import the flag tool if this is not production
if (!isProd && !isHot && !featureFlagToolImported) {
	import('./utils/feature-flag-tool').then(() => {
		console.log('Flag tool imported');
		featureFlagToolImported = true;
	});
}

const fallback = () => (
	<div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#1a1a1f' }}>
		<WaitingIndicator fillSpace />
	</div>
);

const AppContent: React.FC = () => {
	const token = useTypedSelector(state => state.AuthReducer.token);
	const [hostname, setHostname] = useState<string | undefined>(isLocal ? undefined : window.location.hostname);
	const [currentTheme, setCurrentTheme] = useState<EPaletteModes>(EPaletteModes.Light);

	useForceSecureCookies();

	useEffect(() => {
		if (isLocal) {
			import('./utils/mock-hostname').then(({ getMockHostname }: { getMockHostname: () => string }) => {
				setHostname(getMockHostname());
			});
		}
	}, []);

	if (!hostname) {
		return fallback();
	}

	return (
		<div className="App" style={{ pointerEvents: 'auto' }}>
			{
				isGoogleAuth ? (
					<AppContext.Provider value={AppContexts.googleAuth}>
						<Routes>
							<Route path="*" element={<GoogleAuth />} />
						</Routes>
					</AppContext.Provider>
				) : (
					<>
						{(() => {
							// embed routes
							if (isEmbed) {
								return (
									<AppContext.Provider value={AppContexts.embed}>
										<Routes>
											<Route path="/media/:event_uuid/:session_uuid/:language" element={
												<Suspense fallback="">
													<SessionStreamEmbedDataProvider>
														<AuthGate noRedirectOnFail>
															<Suspense fallback={fallback()}>
																<SessionStreamEmbed />
															</Suspense>
														</AuthGate>
													</SessionStreamEmbedDataProvider>
												</Suspense>
											} />
											<Route path="/:event_uuid/:session_uuid/:language" element={
												<Suspense fallback={fallback()}>
													<EmbedChat />
												</Suspense>
											} />
										</Routes>
									</AppContext.Provider>
								);
							}

							if (isCreatePlatformEmbed && token) {
								return (
									<AppContext.Provider value={AppContexts.embed}>
										<FeatureFlagGate>
											<Routes>
												<Route path="*" element={
													<Suspense fallback={fallback()}>
														<EmbedCreatePlatform />
													</Suspense>
												} />
											</Routes>
										</FeatureFlagGate>
									</AppContext.Provider>
								);
							}

							// admin routes
							if (isAdmin) {
								return (
									<AppContext.Provider value={AppContexts.admin}>
										<Suspense fallback="">
											<FullPageProjectThemeGradient />
										</Suspense>
										<FeatureFlagGate>
											<Routes>

												{/* Brandlive Platform rendering components in iframe */}
												<Route path={PATHNAMES.Event.PlatformIframe} element={
													<AuthGate>
														<AppContext.Provider value={AppContexts.liveEvent}>
															<ThemeContext.Provider value={[currentTheme, setCurrentTheme]}>
																<Suspense fallback="">
																	<PlatformComponentRenderer />
																</Suspense>
															</ThemeContext.Provider>
														</AppContext.Provider>
													</AuthGate>
												} />

												{isLocal && (
													<Route path="/icon-list" element={<Suspense fallback=""><IconPreviewer /></Suspense>} />
												)}

												<Route path="/sso-redirect" element={<SSORedirect />} />
												<Route path="/event/:uuid/:language?/*" element={
													<AppContext.Provider value={AppContexts.liveEvent}>
														<ThemeContext.Provider value={[currentTheme, setCurrentTheme]}>
															{/** Will need (nginx?) routing method to forward hostname here - routing tbd, but this will work for now */}
															<LiveEvent isEditor={isEditor} debug={true} />
														</ThemeContext.Provider>
													</AppContext.Provider>
												} />
												<Route path="/google-auth" element={
													<AppContext.Provider value={AppContexts.googleAuth}>
														<GoogleAuth />
													</AppContext.Provider>
												} />
												<Route path="/admin/*" element={
													<AuthGate>
														<Suspense fallback={fallback()}>
															<Admin />
														</Suspense>
													</AuthGate>
												} />
												<Route path={PATHNAMES.ModeratorSession} element={
													<AuthGate>
														<Suspense fallback={fallback()}>
															<Moderator />
														</Suspense>
													</AuthGate>
												} />
												{/* page-not-found route not used in app but provides path to verify component */}
												<Route path="/page-not-found" element={<PageNotFound />} />
												<Route path="/" element={<Home />} />
												<Route path="*" element={<Home />} />
											</Routes>
										</FeatureFlagGate>
									</AppContext.Provider>
								);
							}

							// live event routes
							return (
								<AppContext.Provider value={AppContexts.liveEvent}>
									<Routes>
										<Route path="/sso-redirect" element={<SSORedirect />} />
										<Route path="/:eventName" element={<LiveEventLanguageRedirect />} />
										<Route path="/:eventName/:language/*" element={
											<ThemeContext.Provider value={[currentTheme, setCurrentTheme]}>
												<LiveEvent isEditor={isEditor} host={hostname} eventNamePass={window.location.pathname.split("/")?.[1]} />
											</ThemeContext.Provider>
										} />
									</Routes>
								</AppContext.Provider>
							);
						})()}
					</>
				)
			}
		</div>
	);
};

const router = createBrowserRouter([
	{ path: '*', element: <AppContent /> }
]);

const App: React.FC = () => {
	const token = useTypedSelector(state => state.AuthReducer.token);
	const dispatch = useAppDispatch();

	useForceSecureCookies();

	const updatingEventThemePackMigration = useMigrateColorPackToThemePack(isAdmin);
	const migratingRegistrationGating = useMigrateRegistrationGating(isAdmin);

	useEffect(() => {
		//if the user is signed in on startup, refresh their token first thing to catch any changes
		// Do NOT do this in oAuth flow.  oAuth flow uses a different set of tokens to prevent crossing
		// between multiple appliations.
		if (token && !isOauthFlow) {
			dispatch(refreshToken(token));
			dispatch(getAdminChannels(token));
		}

		if (isAdmin) {
			blAuth.initializeActivityTracker();
		}

		// checks if the page is in the foreground and there is a video playing
		MonitorPageVisibility();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);


	const fallback = useCallback(() => (
		<div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#1a1a1f' }}>
			<WaitingIndicator fillSpace />
		</div>
	), []);

	if (updatingEventThemePackMigration || migratingRegistrationGating) {
		return fallback();
	}

	return (
		<Suspense fallback="">
			<RouterProvider router={router} />
			<Suspense fallback="">
				<LemonadeGradient />
			</Suspense>
			<div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, pointerEvents: 'none', zIndex: 1000 }} id="popover-global-container" className={(!isEditor && !isTestEditor) ? "live-event-fullscreen-popover" : ""} />
			<div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, pointerEvents: 'none' }} id="modal-global-container" className={(!isEditor && !isTestEditor) ? "live-event-modal" : ""} />
			<div id="date-portal" />
			<Suspense fallback="">
				<Uploader />
			</Suspense>
		</Suspense>
	);
};

export default App;
